// app/javascript/projects/components/ProjectsDisplay.jsx

import { DropTarget } from 'react-dnd';
import React, { useState } from 'react';

import { DragItemTypes } from 'constants/DragConstants';
import { canDropOnParent, isValidMerge } from 'utils/nestingHelpers';
import { isOptimistic } from 'hooks/utils/idNormalization';

import DragCodeLabel from './DragCodeLabel'
import MarginDropZone from './MarginDropZone'
import { useRenameCode } from 'hooks/codes/mutations/useRenameCode';
import { useCode } from 'hooks/codes/queries';

// React DnD drop target configuration
const codeTarget = {
  drop(props, monitor) {
    const item = monitor.getItem();
    const didDrop = monitor.didDrop();

    if (!didDrop && !props.ghost) {
      if (canDropOnParent(item.maxDepth, props.level) && props.nestCodes) {
        // Use the nestCodes function passed from the parent component
        props.nestCodes(item.codeID, props.id, 1);
        props.endDrag();
      } else {
        props.rejectNesting();
      }
    }
  }
}

function collectTarget(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({ shallow: true }),
    draggedItem: monitor.getItem(), // Get the dragged item data
    canDrop: monitor.canDrop()
  }
}

function CodeLabelDropZone(props) {
  const [renameMode, setRenameMode] = useState(false);
  
  // Use the rename code mutation hook
  const renameCodeMutation = useRenameCode();
  
  // Fetch code data using the hook
  const { data: codeData } = useCode(props.id);
  
  // Use fetched name or fallback to props.name
  const codeName = props.newCode ? props.name : codeData?.name;
  
  const onRename = () => {
    setRenameMode(true);
  }

  const renameCode = (id, name) => {
    // Use the mutation directly if it's available
    if (renameCodeMutation) {
      renameCodeMutation.mutate({ codeId: id, name });
    }
    
    setRenameMode(false);
  }

  const { connectDropTarget, isOver, draggedItem, canDrop } = props;
  const margin = (props.level - 1) * 30 + "px";
  
  // Check if dragging over self, parent, or grandparent
  const isDraggingOverSelf = draggedItem && (
    draggedItem.codeID === props.id ||
    draggedItem.codeID === props.parent_id ||
    draggedItem.codeID === props.grandparent_id
  );
  
  // Calculate if the dragged item can be dropped on this parent
  const canDropParentValue = draggedItem && !isDraggingOverSelf ? 
    canDropOnParent(draggedItem.maxDepth, props.level) : false;

  // Calculate if the dragged item can be merged (only if it has no children)
  const canMerge = draggedItem && !isDraggingOverSelf ?
    isValidMerge(draggedItem) : false;

  // Only show hover effects when drop is actually valid
  const validDragOver = isOver && canDropParentValue;

  // Only show merge zone when hovering AND merge is valid
  const validMergeHover = isOver && canMerge;

  return connectDropTarget(
    <div>
      <div style={{paddingBottom:"12px", marginLeft: margin}}>
        <DragCodeLabel
            initiateMergeCode={props.initiateMergeCode}
            transitionType={props.transitionType}
            newCode={props.newCode}
            collapsed={props.collapsed}
            count={props.hideCodeCounts ? 0 : codeData?.count || 0}
            position={props.position}
            parent_id={codeData?.parent_id}
            name={codeName}
            color={codeData?.color}
            id={props.id}
            maxDepth={props.maxDepth}
            mergeParent={props.mergeParent}
            showConfirmMerge={props.showConfirmMerge}
                  // TODO: this should be included in codes, this logic is extra
            selected={props.selected}
            onClick={renameMode ? ()=>{} : props.onClick}
            disabled={props.disabled}
            deleteCode={props.deleteCode}
            dragOver={validDragOver}
            canMergeHere={validMergeHover}
            startDrag={props.startDrag}
            endDrag={props.endDrag}
            ghost={props.ghost}
            draggingEnabled={!props.ghost && props.draggingEnabled && !renameMode && !isOptimistic(props.id)}
            onCollapseClicked={props.onCollapseClicked}
            isSearching={props.isSearching}
            cancelMerge={props.cancelMerge}
            confirmMerge={props.confirmMerge}
            onShowMerge={props.onShowMerge}
            allowDropdown={props.allowDropdown}
            renameCode={renameCode}
            onRename={onRename}
            renameMode={renameMode}
            setConfirmDrag={props.setConfirmDrag}
        />
      </div>
      <MarginDropZone
        isOverParent={validDragOver}
        isOverBottom={props.isOverBottom}
        ghost={!!props.ghost}
        rejectNesting={props.rejectNesting}
        nestCodes={props.nestCodes}
        endDrag={props.endDrag}
        noop={props.marginNoOp}
        level={props.level}
        isLastInSiblings={props.isLastInSiblings}
        grandparentPosition={props.grandparentPosition}
        parentPosition={props.parentPosition}
        isParentLastInSiblings={props.isParentLastInSiblings}
        hasChildren={props.hasChildren}
        position={props.position}
        parent_id={props.parent_id}
        grandparent_id={props.grandparent_id}
        codeCollapsed={props.codeCollapsed}
        dropCodeId={props.id}
        canDropOnParent={canDropParentValue}
        draggedItem={draggedItem}
      />
    </div>
  );
}

export default DropTarget(
  DragItemTypes.CODE_LABEL, 
  codeTarget, 
  collectTarget
)(CodeLabelDropZone);